import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Calendar,
  Checkbox,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Modal,
  Radio,
  Toggle,
  Tooltip,
  Typography,
} from "fds";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import { LoginDto } from "../../types/auth";
import { DocInfo, PermissionPolicy } from "../../types/files";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import dayjs from "dayjs";
import { Policy } from "../../types/workgroup";
import { t } from "i18next";
import i18n from "../../locales/i18n";

interface SetPolicyPageProps {
  policy?: PermissionPolicy;
  isInd?: boolean;
  isClient?: boolean;
  page?: "SEND_FILE" | "WORKGROUP";
  targetSettingUserAuth?: Policy;
  docInfo?: DocInfo;
  changePolicy: (policy: PermissionPolicy) => void;
  isNew?: boolean;
  classes?: string;
  isWorkgroupManager?: boolean;
}

const advanced_drm_types = [
  { resrouce: t("file.policy.print"), key: "print" },
  { resrouce: t("file.policy.printScreen"), key: "print_screen" },
  { resrouce: t("file.policy.copyDoc"), key: "extract" },
];
const SetPolicyPage: React.FC<SetPolicyPageProps> = ({
  policy,
  changePolicy,
  docInfo,
  page,
  isInd,
  isWorkgroupManager = true,
  isClient,
  targetSettingUserAuth,
  isNew,
  classes,
}) => {
  const date = new Date();
  const serverTimezone = -date.getTimezoneOffset() / 60;
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const screenWatermark = loginDto
    ? utilsCommon.convertWatermark(
        loginDto?.userInfo.userName,
        loginDto?.userInfo.userId,
        "project.xlsx",
        utilsCommon.renderShortDateFormat(dayjs().valueOf()),
        utilsCommon.renderDateFormat(dayjs().valueOf()),
        loginDto?.ecoUserInfo.remoteAddr,
        loginDto?.ecoCommonConfig.watermarkPattern
      )
    : "";
  /** State 정리 */
  const [stateChage, changeStateChange] = useState(false);
  const [buttonClick, changeButtonClick] = useState(0);
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE");
  /** 함수 정리 */
  const handleChangePolicy = (
    key: string,
    value: boolean | number | string | string[]
  ) => {
    if (key.includes("drmPerm-")) {
      const target = key.split("drmPerm-")[1];
      if (value && [target, ...(policy?.drmPerm ?? [])].length === 4) {
        changeOpenExtraModal("RESTRICT_SELECT_ALL");
        return;
      }
      policy &&
        changePolicy({
          ...policy,
          drmPerm: value
            ? [target, ...(policy?.drmPerm ?? [])]
            : policy?.drmPerm?.filter((item1) => item1 !== target) ?? [],
        });
      return;
    } else if (key === "allowDays") {
      changeStateChange(true);
    }
    policy &&
      changePolicy({
        ...policy,
        drmPerm: key === "wrapping" || key === "download" ? [] : policy.drmPerm,
        wrapping: key === "download" ? false : policy.wrapping,
        currentViewCount:
          key === "allowViewCount" && typeof value === "number"
            ? value
            : policy.currentViewCount,
        startTime: key === "allowDays" ? dayjs().valueOf() : policy.startTime,
        allowDaysTimeZone: serverTimezone,
        [key]: value,
      });
  };
  /** UI: Web support, Mobile support, 그리고 mac support 우측에 나오는 정보 표시*/
  const renderPolicyResourceByType = (type: string) => {
    let resource = "";

    if (type === "webSupport") {
      resource =
        utilsCommon.checkSupportPolicy("Web") === "ADMIN_NOT_ALLOW"
          ? t("file.policy.notPolicy")
          : t("file.policy.notsupport");
    } else if (type === "mobileSupport") {
      resource =
        utilsCommon.checkSupportPolicy("Mobile") === "ADMIN_NOT_ALLOW"
          ? t("file.policy.notPolicy")
          : t("file.policy.notsupport");
    } else if (type === "macSupport") {
      resource =
        utilsCommon.checkSupportPolicy("Mac") === "ADMIN_NOT_ALLOW"
          ? t("file.policy.notPolicy")
          : t("file.policy.notsupport");
    }
    return (
      <Typography
        type="body4_rg"
        exactColor={getColor("COLOR_TEXT_SECONDARY")}
        classes="mg ml-auto"
      >
        {resource}
      </Typography>
    );
  };
  return (
    <Box direction="column">
      <Box alignItems="center">
        <Box direction="column">
          <Typography type="subtitle2">{t("file.policy.revision")}</Typography>
          <Typography
            type="body4_rg"
            classes="mg mt-4"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {page === "SEND_FILE"
              ? t("file.policy.revisionSendFileDesc")
              : t("file.policy.revisionDesc")}
          </Typography>
        </Box>
        <Toggle
          classes="mg ml-auto"
          checked={policy?.revision}
          onChange={() => handleChangePolicy("revision", !policy?.revision)}
        />
      </Box>
      <Box alignItems="center" classes="mg mt-32">
        <Box direction="column">
          <Box alignItems="center">
            <Typography type="subtitle2">
              {t("file.policy.download")}
            </Typography>
            <Tooltip message={t("file.policy.downloadToolTip")} wordBreak>
              <Icons
                variant="fill"
                label="questionmark"
                style={{ display: "flex" }}
                classes="mg ml-4"
              />
            </Tooltip>
          </Box>
          <Typography
            type="body4_rg"
            classes="mg mt-4"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {page === "SEND_FILE"
              ? t("file.policy.downloadSendFileDesc")
              : t("file.policy.downloadDesc")}
          </Typography>
        </Box>
        <Toggle
          classes="mg ml-auto"
          checked={policy?.download}
          onChange={() =>
            handleChangePolicy("download", !policy?.download || false)
          }
        />
      </Box>
      {policy?.download &&
        utilsCommon.checkVisibleOptions("advandedPolicy") && (
          <Box classes="mg mt-20">
            <Box
              direction="column"
              alignItems="flex-start"
              width={!policy.wrapping ? "100%" : "calc(100% - 216px)"}
            >
              <Box alignItems="center">
                <Checkbox
                  label={t("file.policy.advanded")}
                  check={policy.wrapping}
                  onClick={() =>
                    handleChangePolicy("wrapping", !policy.wrapping)
                  }
                />
                <Tooltip message={t("file.policy.advandedTooltip")}>
                  <Icons
                    variant="fill"
                    label="questionmark"
                    style={{ display: "flex" }}
                    classes="mg ml-4"
                  />
                </Tooltip>
              </Box>
              <Tooltip
                message={
                  t("file.policy.supportExtensions") +
                  ": " +
                  [
                    ...new Set([
                      ...(loginDto?.serviceConfig?.revisionExtensions || []),
                      ...(loginDto?.serviceConfig?.manualRevisionExtensions ||
                        []),
                    ]),
                  ]
                    .filter((ext) => !!ext)
                    .map((ext) => ext)
                    .sort((a, b) => (a < b ? -1 : 1))
                    .join(", ")
                }
              >
                <Typography
                  type="body4_rg"
                  classes="mg ml-28"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  ellipsis
                >
                  {t("file.policy.supportExtensions") +
                    ": " +
                    [
                      ...new Set([
                        ...(loginDto?.serviceConfig?.revisionExtensions || []),
                        ...(loginDto?.serviceConfig?.manualRevisionExtensions ||
                          []),
                      ]),
                    ]
                      .filter((ext) => !!ext)
                      .map((ext) => ext)
                      .sort((a, b) => (a < b ? -1 : 1))
                      .join(", ")}
                </Typography>
              </Tooltip>
            </Box>
            {policy.wrapping && (
              <Dropdown
                placeholder=";"
                value={
                  policy.drmPerm?.length === 0
                    ? t("file.policy.normal")
                    : t("file.policy.advanced")
                }
                width={200}
                classes="mg ml-auto"
                buttonClick={buttonClick}
              >
                <DropdownItem
                  label={t("file.policy.advanced")}
                  checkColor={!!policy?.drmPerm && policy.drmPerm?.length > 1}
                  onClick={() => {
                    handleChangePolicy("drmPerm", ["view"]);
                    changeButtonClick(buttonClick + 1);
                  }}
                />
                <DropdownItem
                  label={t("file.policy.normal")}
                  checkColor={policy.drmPerm?.length === 0}
                  onClick={() => {
                    handleChangePolicy("drmPerm", []);
                    changeButtonClick(buttonClick + 1);
                  }}
                />
              </Dropdown>
            )}
          </Box>
        )}
      {policy?.drmPerm && policy?.drmPerm?.length > 0 && policy?.download && (
        <Box classes="advanced_items">
          {advanced_drm_types.map((item) => (
            <Box>
              <Typography
                type="body2_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {item.resrouce}
              </Typography>
              <Box classes="mg ml-auto item">
                <Radio
                  label={t("common.allow")}
                  style={{ width: i18n.language === "ko" ? 100 : 110 }}
                  check={policy.drmPerm?.includes(item.key)}
                  onClick={() =>
                    handleChangePolicy("drmPerm-" + item.key, true)
                  }
                />
                <Radio
                  label={t("common.notAllow")}
                  style={{ width: i18n.language === "ko" ? 100 : 110 }}
                  check={!policy.drmPerm?.includes(item.key)}
                  onClick={() =>
                    handleChangePolicy("drmPerm-" + item.key, false)
                  }
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {utilsCommon.checkVisibleOptions("readRestrict") && (
        <Box direction="column">
          <Box direction="column" classes="mg mt-32">
            <Box alignItems="center">
              <Typography type="subtitle2">
                {t("file.policy.readRestrict")}
              </Typography>
              <Tooltip message={t("file.policy.restrictToolTip")}>
                <Icons
                  variant="fill"
                  label="questionmark"
                  style={{ display: "flex" }}
                  classes="mg ml-4"
                />
              </Tooltip>
            </Box>

            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.policy.restrictInfo")}
            </Typography>
          </Box>{" "}
          <Box classes="mg mt-20" width={"100%"} alignItems="start">
            <Checkbox
              label={t("file.policy.allowCount")}
              check={policy && policy?.allowViewCount > 0}
              onClick={() =>
                handleChangePolicy(
                  "allowViewCount",
                  policy?.allowViewCount === 0 ? 20 : 0
                )
              }
            />
            <Box classes="mg ml-auto" alignItems="center">
              <InputField
                placeholder={""}
                disabled={
                  !policy?.allowViewCount || policy?.allowViewCount <= 0
                }
                width={100}
                maxLength={3}
                size="md"
                value={
                  policy?.allowViewCount === 0
                    ? ""
                    : policy?.allowViewCount + ""
                }
                type="positive-integer"
                onChange={(e) =>
                  handleChangePolicy(
                    "allowViewCount",
                    e.target.value ? parseInt(e.target.value) : 0
                  )
                }
              />
              <Typography
                textAlign="end"
                type="body2_rg"
                exactColor={getColor(
                  policy?.allowViewCount === undefined ||
                    policy?.allowViewCount <= 0
                    ? "COLOR_TEXT_DISABLED"
                    : "COLOR_TEXT_PRIMARY"
                )}
                width={i18n.language !== "ko" ? "47px" : "25px"}
              >
                {t("file.policy.count")}
              </Typography>
            </Box>
          </Box>
          <Box classes="mg mt-20" width={"100%"} alignItems="start">
            <Checkbox
              label={
                isInd
                  ? t("file.policy.allowExpiration")
                  : t("file.policy.allowPeriod")
              }
              check={
                policy &&
                policy?.allowDays !== undefined &&
                policy?.allowDays > (isNew || !isInd ? -1 : 0)
              }
              onClick={() =>
                handleChangePolicy(
                  "allowDays",
                  policy && policy?.allowDays && policy?.allowDays <= 0
                    ? 365
                    : -1
                )
              }
            />
            <Box direction="column" classes="mg ml-auto">
              <Box alignItems="center">
                {isInd ? (
                  <Calendar
                    type={"single"}
                    width={200}
                    popperPlacement={"top-end"}
                    placeholder={" "}
                    disabled={
                      policy &&
                      (policy?.allowDays === undefined ||
                        policy?.allowDays <= (isNew ? -1 : 0))
                    }
                    isClient={isClient}
                    minDate={new Date()}
                    maxDate={new Date(dayjs().add(999, "d").valueOf())}
                    hasTimezone
                    selected={
                      policy &&
                      policy?.allowDays !== undefined &&
                      policy.allowDays > (isNew ? -1 : 0)
                        ? new Date(
                            dayjs(
                              stateChage
                                ? undefined
                                : targetSettingUserAuth &&
                                  targetSettingUserAuth.uniqueId &&
                                  targetSettingUserAuth.targetType === "USER"
                                ? dayjs(policy.startTime)
                                    .set("hour", 0)
                                    .valueOf()
                                : docInfo?.createTime
                            )
                              .add(
                                targetSettingUserAuth
                                  ? policy.allowDays - 1
                                  : policy.allowDays,
                                "d"
                              )
                              .valueOf()
                          )
                        : undefined
                    }
                    onSubmit={(date) =>
                      handleChangePolicy(
                        "allowDays",
                        dayjs(date instanceof Date ? date : date.start)
                          .add(targetSettingUserAuth ? 1 : 0, "day")
                          .set("hour", 23)
                          .set("minute", 59)
                          .set("second", 59)
                          .diff(
                            utilsCommon.getStartDay(new Date()).valueOf(),
                            "d"
                          )
                      )
                    }
                  />
                ) : (
                  <InputField
                    placeholder={""}
                    disabled={!!policy?.allowDays && policy?.allowDays < 0}
                    width={100}
                    size="md"
                    maxLength={3}
                    value={
                      !policy ||
                      policy?.allowDays === undefined ||
                      policy?.allowDays <= -1
                        ? ""
                        : policy?.allowDays + ""
                    }
                    type="positive-integer"
                    onChange={(e) => {
                      handleChangePolicy(
                        "allowDays",
                        e.target.value ? parseInt(e.target.value) : 0
                      );
                    }}
                  />
                )}
                {!isInd && (
                  <Typography
                    textAlign="end"
                    type="body2_rg"
                    width={i18n.language !== "ko" ? "47px" : "25px"}
                    exactColor={getColor(
                      !policy ||
                        policy?.allowDays === undefined ||
                        policy?.allowDays <= -1
                        ? "COLOR_TEXT_DISABLED"
                        : "COLOR_TEXT_PRIMARY"
                    )}
                  >
                    {t("common.days")}
                  </Typography>
                )}
              </Box>

              {!isInd && (
                <Typography
                  type="body4_rg"
                  exactColor={getColor(
                    !policy ||
                      policy?.allowDays === undefined ||
                      policy?.allowDays <= -1
                      ? "COLOR_TEXT_DISABLED"
                      : "COLOR_TEXT_PRIMARY"
                  )}
                  classes="mg mt-4"
                >
                  ({t("file.policy.dependDate")})
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
      {utilsCommon.checkVisibleOptions("webview") && (
        <Box classes="mg mt-32" direction="column">
          <Box alignItems="center">
            <Box direction="column">
              <Typography type="subtitle2">
                {t("file.policy.webSupport")}
              </Typography>
              <Typography
                type="body4_rg"
                classes="mg mt-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("file.policy.webSupportDesc")}
              </Typography>
            </Box>
            {utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED" ? (
              <Toggle
                classes="mg ml-auto"
                checked={policy?.webView}
                onChange={() => handleChangePolicy("webView", !policy?.webView)}
              />
            ) : (
              renderPolicyResourceByType("webSupport")
            )}
          </Box>
          {utilsCommon.checkSupportPolicy("Web") === "SUPPORT_ACCEPTED" &&
            policy?.webView && (
              <Box
                direction="column"
                alignItems="flex-start"
                classes="mg mt-20"
              >
                <Checkbox
                  label={t("file.policy.screenWatermark")}
                  check={loginDto.ecoCommonConfig.watermark !== "NONE"}
                  disable
                />
                {loginDto.ecoCommonConfig.watermark !== "NONE" && (
                  <Box classes="screenWaterMark">
                    <Tooltip message={screenWatermark}>
                      <Typography ellipsis type="body2_rg">
                        {screenWatermark}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}
        </Box>
      )}
      {utilsCommon.checkVisibleOptions("mobile") && (
        <Box alignItems="center" classes="mg mt-32">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("file.policy.mobileSupport")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.policy.mobileSupportDesc")}
            </Typography>
          </Box>
          {utilsCommon.checkSupportPolicy("Mobile") === "SUPPORT_ACCEPTED" ? (
            <Toggle classes="mg ml-auto" checked disabled />
          ) : (
            renderPolicyResourceByType("mobileSupport")
          )}
        </Box>
      )}
      {utilsCommon.checkVisibleOptions("mac") && (
        <Box alignItems="center" classes="mg mt-32">
          <Box direction="column">
            <Typography type="subtitle2">
              {t("file.policy.macSupport")}
            </Typography>
            <Typography
              type="body4_rg"
              classes="mg mt-4"
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("file.policy.macSupportDesc")}
            </Typography>
          </Box>
          {utilsCommon.checkSupportPolicy("Mac") === "SUPPORT_ACCEPTED" ? (
            <Toggle classes="mg ml-auto" checked disabled />
          ) : (
            renderPolicyResourceByType("macSupport")
          )}
        </Box>
      )}
      {utilsCommon.checkVisibleOptions(
        "openSetting",
        page,
        isWorkgroupManager
      ) &&
        !targetSettingUserAuth && (
          <Box alignItems="center" classes="mg mt-32">
            <Box direction="column">
              <Typography type="subtitle2">
                {t("file.policy.internalAllow")}
              </Typography>
              <Typography
                type="body4_rg"
                classes="mg mt-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("file.policy.internalAllowDesc")}
              </Typography>
            </Box>
            <Toggle
              classes="mg ml-auto"
              checked={policy?.additionalViewPermission === "INTERNAL_USER"}
              onChange={() =>
                handleChangePolicy(
                  "additionalViewPermission",
                  policy?.additionalViewPermission === "INTERNAL_USER"
                    ? "NONE"
                    : "INTERNAL_USER"
                )
              }
            />
          </Box>
        )}
      {openExtraModal === "RESTRICT_SELECT_ALL" && (
        <Modal
          open={true}
          onClose={() => changeOpenExtraModal("CLOSE")}
          footer={
            <Box>
              <Button
                label={t("common.close")}
                btnStyle="fill"
                onClick={() => changeOpenExtraModal("CLOSE")}
              />
            </Box>
          }
          title={t("file.policy.chooseNormal")}
          width={540}
          bodyMsg={t("file.policy.chooseNormalInfo")}
        />
      )}
    </Box>
  );
};
export default SetPolicyPage;
